import { GlobalInvoiceProvider } from 'invoices/context/GlobalInvoiceContext';
import React, { FC } from 'react';
import { ApolloProvider } from '@apollo/client';
import DateFnsUtils from '@date-io/date-fns';
import { apolloClient } from 'utils/gql/apollo/ApolloClient';
import { ColumnConfigurationsProvider } from 'utils/hooks/useDataTableColumnConfigurations/ColumnConfigurationContext';
import { OcDarklyProvider } from 'integrations/launchDarkly/providers/OcDarklyProvider';
import { GlobalDarklyProvider } from 'integrations/launchDarkly/providers/GlobalDarklyProvider';
import MixpanelProvider from 'integrations/mixpanel/MixpanelProvider';
import { createBrowserHistory, History } from 'history';
import { Router } from 'react-router-dom';
import { ConfirmDialogProvider } from 'common/ConfirmDialog/ConfirmDialogProvider';
import { SettingsServiceProvider } from 'common/settingsService/impl/provider/SettingsServiceProvider';
import { MuiPickersUtilsProvider, MuiThemeProvider, Theme } from '@utmost.co/utmost-design-system';
import { AuthProvider } from 'src/components/Authentication/AuthProvider';
import { IntlProvider } from './IntlProvider/IntlProvider';
import SnackbarCustomProvider from './SnackBarProvider/SnackbarCustomProvider';
import { Tutorials } from './tutorials/Tutorials';
import { NotificationsProvider } from './Notifications/NotificationsContext';
import { TaskNotificationsProvider } from './Tasks/TaskNotificationsContext';
import { TenantProvider } from './Tenant/TenantProvider';
import { CustomTenantModel } from './Tenant/Tenant.types';
import { AuthUser } from './User/AuthUser';
import { OcDateTimeFormatProvider } from './OcDateTimeFormatProvider';
import { DesignTokensProvider } from './DesignTokensProvider';
import { EnterpriseUdsRoutingProvider } from 'providers/UdsRoutingProvider/EnterpriseUdsRoutingProvider';
import { AuthUser as AuthUserModel } from './User/AuthUserModels';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { theme } from '@utmost.co/utmost-design-system/src/theme/MaterialTheme';
import { createTheme } from '@utmost.co/utmost-design-system/src/components/MaterialUtils';
import { BpDesignThemeProvider, createBpDesignTheme } from '@beeline.com/bp-design-system';
import { MixpanelIdentify } from 'integrations/mixpanel/MixpanelIdentify';
import { SmartFormFlagsSync } from 'smartforms/utils/SmartFormFlagsSync';
// biome-ignore lint/style/noRestrictedImports: todo exception but should be moved to BPDS
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// biome-ignore lint/style/noRestrictedImports: todo exception but should be moved to BPDS
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
const bpDesignTheme = createBpDesignTheme(
  process.env.NODE_ENV === 'test'
    ? {
        transitions: {
          create: () => 'none'
        }
      }
    : undefined
);
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { PermissionProvider } from 'providers/SmartformPermission/PermissionProvider';
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.updateLocale('en', {
  weekStart: 1
});

export const bpeTheme = createTheme({
  ...theme,
  overrides: {
    ...theme.overrides,
    MuiInputLabel: {
      ...theme.overrides?.MuiInputLabel,
      root: {
        ...theme.overrides?.MuiInputLabel?.root,
        color: tokens.colorOnSurface
      },
      shrink: {
        ...theme.overrides?.MuiInputLabel?.shrink,
        color: tokens.colorOnSurface
      }
    }
  }
});

interface AppProvidersProps {
  initialUser?: AuthUserModel;
  history?: History;
  tenant: CustomTenantModel | undefined;
  messages: Record<string, string> | undefined;
  theme?: Theme;
  timezones?: string[];
}

const AppProviders: FC<React.PropsWithChildren<AppProvidersProps>> = ({
  initialUser,
  children,
  history,
  tenant,
  messages,
  theme,
  timezones
}) => {
  return (
    <ApolloProvider client={apolloClient}>
      <IntlProvider messages={messages}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* @ts-ignore this component does not support "children" prop properly */}
          <Router history={history || createBrowserHistory()}>
            <EnterpriseUdsRoutingProvider>
              <BpDesignThemeProvider theme={bpDesignTheme}>
                <MuiThemeProvider theme={theme ?? bpeTheme}>
                  <SnackbarCustomProvider>
                    <TenantProvider tenant={tenant}>
                      <GlobalDarklyProvider
                        userUid={initialUser?.uid}
                        personUid={initialUser?.person?.personUid}
                        webOrigin={tenant?.tenantConfig?.webOrigin}
                      >
                        <OcDarklyProvider
                          userUid={initialUser?.uid}
                          personUid={initialUser?.person?.personUid}
                          webOrigin={tenant?.tenantConfig?.webOrigin}
                        >
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <AuthProvider>
                              <AuthUser user={initialUser}>
                                <Tutorials>
                                  <OcDateTimeFormatProvider timezones={timezones}>
                                    <ColumnConfigurationsProvider>
                                      <ConfirmDialogProvider>
                                        <MixpanelProvider>
                                          <MixpanelIdentify />
                                          <SmartFormFlagsSync />
                                          <NotificationsProvider userUid={initialUser?.uid}>
                                            <TaskNotificationsProvider userUid={initialUser?.uid}>
                                              <DesignTokensProvider>
                                                <SettingsServiceProvider fetch={!!initialUser?.uid}>
                                                  <GlobalInvoiceProvider>
                                                    <PermissionProvider>{children}</PermissionProvider>
                                                  </GlobalInvoiceProvider>
                                                </SettingsServiceProvider>
                                              </DesignTokensProvider>
                                            </TaskNotificationsProvider>
                                          </NotificationsProvider>
                                        </MixpanelProvider>
                                      </ConfirmDialogProvider>
                                    </ColumnConfigurationsProvider>
                                  </OcDateTimeFormatProvider>
                                </Tutorials>
                              </AuthUser>
                            </AuthProvider>
                          </MuiPickersUtilsProvider>
                        </OcDarklyProvider>
                      </GlobalDarklyProvider>
                    </TenantProvider>
                  </SnackbarCustomProvider>
                </MuiThemeProvider>
              </BpDesignThemeProvider>
            </EnterpriseUdsRoutingProvider>
          </Router>
        </LocalizationProvider>
      </IntlProvider>
    </ApolloProvider>
  );
};
export default AppProviders;
